import * as Actions from '../actions/actions';
import { h } from 'virtual-dom';
import { sendRequest } from '../../../lib-alme-request/src/lib-alme-request';
import {DefaultSettings, settings} from "../../../../settings/settings-util";
import {AlmeState} from "../../../../wireup/AlmeState";
import {Store} from "redux";
import {Dictionary, Mutator} from "../../../../types/rendering";
import objectPath from 'object-path';
import { AlmeUIInput } from '../../../../types/input';
import { disableInputBox } from '../../../lib-inputbox-toggle/lib/lib-inputbox-toggle';
import {
    isLiveChat,
} from '../../../lib-livechat/lib/lib-livechat'
export default class Renderers {
	public _settings: DefaultSettings;
	public _store: Store<AlmeState> | null = null;
	public _containerMutators: Mutator[] = [];
	public _inputMutators: Mutator[] = [];
	public _buttonMutators: Mutator[] = [];
	public _children: any;
	public value!: string;
    public _suggestion: any;
    public isLiveChat: any;

    constructor(passedSettings?: DefaultSettings, suggestion?: []) {
        this._settings = passedSettings || settings;
        this._suggestion = suggestion;
        this.isLiveChat = isLiveChat;
    }

    initialize(store: Store<AlmeState>, containerMutators : Mutator[] = [], inputMutators : Mutator[] = [], buttonMutators : Mutator[] = [], children = []):void {
        this._store = store;
        this._containerMutators = containerMutators;
        this._inputMutators = inputMutators;
        this._buttonMutators = buttonMutators;
        this._children = children;
    }

    getRender() {
        return (): VirtualDOM.VNode => {
            const inputBaseLoc = this._settings.stateLocations.Window.Input.Base;
            const currentInputLoc = this._settings.stateLocations.Window.Input.CurrentInput;            
            const inputDisabled = this.getInputBoxDisabledState();

            const disabledText = this._settings.textConstants.inputboxDisabledPlaceholderText || settings.textConstants.inputboxDisabledPlaceholderText;

            //Added for support of foreign languages where keys have different functions
            const submitKey = this._settings.textConstants.submitKey || settings.textConstants.submitKey;

            const submit = this.submit.bind(this);
            let displayRemainingTextCount;
            if (!this._settings.footerCounterSettings.hidden) {
             displayRemainingTextCount = this.displayRemainingTextCount.bind(this);
            }
            const toggleErrorToState = this.toggleErrorToState.bind(this);

            const accessibility = this._settings.accessibility || {};
            const arias = accessibility.aria || {};
            const ariasInputLabel = arias.inputBox || settings.accessibility.aria.inputBox;
            const errorText = this._settings.textConstants.inputboxErrorText || settings.textConstants.inputboxErrorText;

            const errorClassHidden = this._settings.classNames.inputboxErrorHidden;
            const errorBox = h("div", { className: errorClassHidden, 'aria-label': errorText }, [errorText]);

            let suggestionDivParameters = {
                id: this._settings.elementIds.autocompleteResults,
            } as any;

            let inputParameters =
            {
                className: this._settings.classNames.inputboxTextAreaClassName,
                maxLength: this._settings.inputBox.maxLength,
                onblur: (): void =>{
                    setTimeout(() => {
                        const value = this.value;
                        this._store?.dispatch(Actions.updateCurrentInput(value, `${inputBaseLoc}.${currentInputLoc}`));
                    }, 0);
                },
                oninput: () =>{
                    setTimeout(() => {
                        if (!this._settings.footerCounterSettings.hidden) {
                         displayRemainingTextCount();
                        }
                    }, 0);
                },
                onkeydown:(e: KeyboardEvent)=>{
                    if (this._settings.predictiveSearchSuggestions.enabled) {
                        setTimeout(() => {
                            this.navigateSuggestions(e);
                        }, 0);
                    }
                },
                onkeyup: (e: KeyboardEvent) =>{
                    setTimeout(() => {
                        if (e.key === submitKey) {
                            submit();
                        }
                        else {
                            toggleErrorToState(false);
                        }
                    }, 0);
                },
                onfocus: (): void => {
                    const value = this.value;
                    this._store?.dispatch(Actions.updateCurrentInput(value, `${inputBaseLoc}.${currentInputLoc}`));
                },
                attributes:
                {
                    type: 'text',
                    placeholder: this._settings.textConstants.inputboxPlaceholderText,
                    autocomplete: 'off',
                    'aria-label': ariasInputLabel,
                },
            } as any;

            let compParameters =
            {
                className: this._settings.classNames.inputboxClassName,
            } as Dictionary;

            let submitParameters =
            {
                attributes:
                {
                    role: 'button',
                    type: 'button',
                },
                className: this._settings.classNames.inputboxSubmitClassName,
                onclick: (e) => {
                    setTimeout(() => {
                        submit();
                    }, 0);
                },
            } as Dictionary;

            if (inputDisabled) {
                inputParameters.attributes["disabled"] = true;
                inputParameters.attributes["placeholder"] = disabledText;
                inputParameters.className = `${this._settings.classNames.inputboxTextAreaClassName} ${this._settings.classNames.inputboxDisabledClassName}`;

                (submitParameters.attributes as Dictionary)['disabled'] = true;
            }

            this._containerMutators.forEach((mutate) => {
                compParameters = mutate(compParameters);
            });

            this._inputMutators.forEach((mutate) => {
                inputParameters = mutate(inputParameters);
            });

            this._buttonMutators.forEach((mutate) => {
                submitParameters = mutate(submitParameters);
            });
            const personaNode = h('div', {ariaHidden:true,className: this._settings.classNames.inputboxCharacterCounter, innerHTML: `0/${this._settings.inputBox.maxLength}`, ariaLabel: `0/${this._settings.inputBox.maxLength}`}, []);
            
            const submitContent = typeof this._settings.textConstants.submitContent === "function" 
                ? this._settings.textConstants.submitContent() : this._settings.textConstants.submitContent; 

            const childNodes = [
                errorBox,                
                this._settings.predictiveSearchSuggestions.enabled ? h('div', suggestionDivParameters) : '',
                h('input', inputParameters),               
                h('button', submitParameters, [submitContent]),
                personaNode,
            ];            

            this._children.forEach((render) => {
                childNodes.push(render());
            });

            return h('div', compParameters, childNodes);
        };
    }

    submit(): void {
        const messageType = this._settings.messageConstants.converseRequest;
        const stateLoc = this._settings.stateLocations;

        const inputBaseLoc = stateLoc.Window.Input.Base;
        const currentInputLoc = stateLoc.Window.Input.CurrentInput;

        const textarea = this.getTextArea();
        const text = textarea.value;
        if (text && text.trim()) {

            // Changing input box state to disable
            disableInputBox(this._store, this._settings, () => true);
            const input: AlmeUIInput = { text, messageType, origin: 'Typed' };
            const prom = Promise.resolve(sendRequest(input));
            textarea.value = '';
            
            // Reset input value in both DOM and state
            prom.then(() => {
                if (!this.getInputBoxDisabledState()) {
                    this.updateInputBoxFocus();
                    this._store?.dispatch(Actions.updateCurrentInput('', `${inputBaseLoc}.${currentInputLoc}`));
                }
            });
        }
        else {
            this.toggleErrorToState(true);
        }
        if (!this._settings.footerCounterSettings.hidden) {
        this.displayRemainingTextCount();
        }
    }

    updateInputBoxFocus(): void {
        setTimeout(() => {
            const theInputBox = this.getTextArea();
            if (theInputBox) {
                if (this.isDesktop()) {
                theInputBox.focus();
                }
                else{
                    theInputBox.blur();
                }
            }
        }, 0);
    }
    
    displayRemainingTextCount(): void {
        let textValue = this.getTextArea().value;
        let textLength = textValue.length;
        let maxLength = this._settings.inputBox.maxLength;
        if(this.isLiveChat(this._store, this._settings))
        {
            maxLength = this._settings.livechat.maxInputLength;
        }
        if (this._settings.predictiveSearchSuggestions.enabled) {
            this._suggestion.then(result => {
                let suggestionSelect = document.getElementById(this._settings.elementIds.autocompleteResults);
                if(suggestionSelect)
                    suggestionSelect.className = this._settings.classNames.inputboxSuggestionClassName
                
                    let filteredSuggestions;
                let content: any;

                if (suggestionSelect)
                    suggestionSelect.innerHTML = '';                

                if (textValue.length > 0) {
                    filteredSuggestions = result.filter(res => res.toLowerCase().includes(textValue.toLowerCase()));
                   
                    for (let i = 0; i < Math.min(filteredSuggestions.length, this._settings.predictiveSearchSuggestions.maxSuggestion); i++) {
                        const suggestionOption = document.createElement('div');
                        suggestionOption.textContent = filteredSuggestions[i];
                        suggestionOption.className = settings.classNames.inputboxSuggestionListClassName;
                      
                        if (textValue !== '') {
                            content = suggestionOption.textContent?.replace(new RegExp(textValue, 'gi'), match => '<span class="' + settings.classNames.inputboxSuggestionHighlightClassName + '">' + match + '</span>');
                            suggestionOption.innerHTML = content;
                        }

                        suggestionOption.addEventListener('click', function () {
                            let input = document.getElementsByClassName(settings.classNames.inputboxTextAreaClassName)[0] as HTMLInputElement
                            const spans = suggestionOption.querySelectorAll(`.${settings.classNames.inputboxSuggestionHighlightClassName}`);

                            spans.forEach(span => {
                                while (span.firstChild) {
                                    suggestionOption.insertBefore(span.firstChild, span);
                                }
                                span.parentNode?.removeChild(span);
                            });

                            input.value = this.innerHTML;
                            while (suggestionSelect?.firstChild) {
                                suggestionSelect.removeChild(suggestionSelect.firstChild);
                            }

                            if (input.value.length > maxLength) {
                                input.value = textValue.substring(0, maxLength);
                                textLength = maxLength;
                            }

                            const textCountClass = settings.classNames.inputboxCharacterCounter;
                            const textCountArea = document.getElementsByClassName(textCountClass)[0];
                            textCountArea.innerHTML = `${input.value.length}/${maxLength}`;
                            textCountArea.setAttribute('aria-label', `${input.value.length}/${maxLength}`);
                            textCountArea.setAttribute('aria-hidden', 'true');
                            input.focus();

                            if (suggestionSelect)
                                suggestionSelect.style.display = 'none';
                        });

                        suggestionSelect?.appendChild(suggestionOption);
                    }

                    if (filteredSuggestions.length > 0) {
                        if (suggestionSelect)
                            suggestionSelect.style.display = 'block';
                    }
                    else {
                        if (suggestionSelect)
                            suggestionSelect.style.display = 'none';
                    }
                }
            });
        }

        //android fix for not respecting max length
        if (textLength > maxLength) {
            this.getTextArea().value = textValue.substring(0, maxLength);
            textLength = maxLength;
        }

        const textCountClass = this._settings.classNames.inputboxCharacterCounter;
        const textCountArea = document.getElementsByClassName(textCountClass)[0];
        textCountArea.innerHTML = `${textLength}/${maxLength}`;
        textCountArea.setAttribute('aria-label', `${textLength}/${maxLength}`);
        textCountArea.setAttribute('aria-hidden', 'true');
    }

    navigateSuggestions(event) {
        const suggestions = document.getElementsByClassName(settings.classNames.inputboxSuggestionListClassName);
        let currentIndex: any;
        let input: any;           
        let newIndex;
       
        currentIndex = this._store?.getState()?.Window?.currentIndex ?? -1 ;
        const currentIndexLoc = this._settings.stateLocations.Window.currentIndex;
        let suggestionSelect = document.getElementById(this._settings.elementIds.autocompleteResults);
      
        if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
            event.preventDefault();
            newIndex = this.calculateNewIndex(event.code, currentIndex, suggestions.length);
            this.hightlightSelected(newIndex);
        }

        this._store?.dispatch(Actions.setCurrentIndex(newIndex, `${currentIndexLoc}`));

        if (event.code === 'Enter' && currentIndex !== -1) {
            input = document.getElementsByClassName(settings.classNames.inputboxTextAreaClassName)[0] as HTMLInputElement;
            input.value = suggestions[currentIndex].textContent;           
            if (suggestionSelect)
                suggestionSelect.style.display = 'none';
        }

        if(event.code === "Tab" || event.code === "Escape"){
            if (suggestionSelect)
                suggestionSelect.style.display = 'none';
        }
    }

    calculateNewIndex(key, currentIndex, maxIndex) {
        if (key === 'ArrowUp') {
            return currentIndex > 0 ? currentIndex - 1 : maxIndex - 1;
        } else if (key === 'ArrowDown') {
            return currentIndex < maxIndex - 1 ? currentIndex + 1 : 0;
        }
    }

    hightlightSelected(currentIndex) {
        const suggestions = document.getElementsByClassName(settings.classNames.inputboxSuggestionListClassName);

        for (let i = 0; i < suggestions.length; i++) {
            suggestions[i].classList.remove('highlight');
        }

        if (currentIndex !== -1) {
            suggestions[currentIndex].classList.add('highlight');
        }
    }

    getTextArea(): HTMLInputElement {
        const inputBoxClass = this._settings.classNames.inputboxTextAreaClassName;

        return document.getElementsByClassName(inputBoxClass)[0] as HTMLInputElement;
    }

    isDesktop(): boolean {
        const deviceType = objectPath.get(this._store?.getState(), this._settings.stateLocations.DeviceType);

        if (!!deviceType && !!this._settings.mobileConstants && !!this._settings.mobileConstants.deviceTypes) {
            return deviceType === this._settings.mobileConstants.deviceTypes.desktop;
        }

        return true;
    }

    toggleErrorToState(on): void{
        const existingClass = on ? this._settings.classNames.inputboxErrorHidden : this._settings.classNames.inputboxError;
        const newClass = on ? this._settings.classNames.inputboxError : this._settings.classNames.inputboxErrorHidden;
        const error = document.querySelector(`.${existingClass}`);

        if (error) { error.className = newClass; }
    }

    getInputBoxDisabledState(): boolean{
            const windowBaseLoc = this._settings.stateLocations.Window.Base;
            const inputBoxLockedLocation = this._settings.stateLocations.Window.InputBoxLocked?.Base;
            const isInputBoxLockedLocation = this._settings.stateLocations.Window.InputBoxLocked?.isInputBoxLocked;

            if (windowBaseLoc && inputBoxLockedLocation && isInputBoxLockedLocation) {
                return this._store?.getState()[windowBaseLoc][inputBoxLockedLocation][isInputBoxLockedLocation] || false;
            }

        return false;
    }

}



// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/window-inputbox/src/renderers/renderers.ts